import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, adminApi, sessionTypeApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import Pagination from '../components/Pagination';
import '../styles/admin-dashboard.css';

const POPULAR_SESSION_ICONS = ['🛋️', '💻', '🏥', '📞', '🎧', '👥', '🧠', '🌿', '🩺', '🤝', '🌟', '🧘', '💬', '📱'];

const DURATION_PRESETS = [30, 45, 50, 60, 90];

const MODE_TITLES = {
  'in-person': 'In-Person (Clinic)',
  'video': 'Video Consultation',
  'call': 'Audio Call',
};

function AdminSessionTypesPage() {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [sessionTypes, setSessionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [modeFilter, setModeFilter] = useState('all'); // 'all', 'in-person', 'video', 'call'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    sessionMode: 'video',
    description: '',
    icon: '💻',
    defaultDuration: 50,
    requiresLocation: false,
    displayOrder: 0,
    isActive: true,
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, sessionType: null, loading: false });

  // Notification Toast
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback({ type: '', message: '' }), 4000);
  };

  const fetchSessionTypes = useCallback(async () => {
    try {
      setLoading(true);
      let list = [];
      try {
        const res = await adminApi.getSessionTypes({ all: 'true' });
        if (Array.isArray(res?.sessionTypes)) list = res.sessionTypes;
        else if (Array.isArray(res?.data)) list = res.data;
        else if (Array.isArray(res)) list = res;
      } catch (adminErr) {
        console.warn('adminApi.getSessionTypes error, trying sessionTypeApi:', adminErr);
        try {
          const publicRes = await sessionTypeApi.getPublicSessionTypes({ all: 'true' });
          if (Array.isArray(publicRes?.sessionTypes)) list = publicRes.sessionTypes;
          else if (Array.isArray(publicRes?.data)) list = publicRes.data;
          else if (Array.isArray(publicRes)) list = publicRes;
        } catch (pubErr) {
          console.warn('sessionTypeApi error, trying direct fetch:', pubErr);
          const directRes = await fetch('/api/session-types?all=true');
          const directData = await directRes.json();
          if (Array.isArray(directData?.sessionTypes)) list = directData.sessionTypes;
        }
      }

      setSessionTypes(list);
    } catch (err) {
      console.error('Error fetching session types:', err);
      showFeedback('error', err.message || 'Failed to load session types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessionTypes();
  }, [fetchSessionTypes]);

  // Statistics
  const totalCount = sessionTypes.length;
  const inPersonCount = sessionTypes.filter((s) => s.sessionMode === 'in-person').length;
  const videoCount = sessionTypes.filter((s) => s.sessionMode === 'video').length;
  const callCount = sessionTypes.filter((s) => s.sessionMode === 'call').length;
  const activeCount = sessionTypes.filter((s) => s.isActive !== false).length;

  // Filtered List
  const filteredSessionTypes = sessionTypes.filter((item) => {
    const q = searchQuery.trim().toLowerCase();
    const modeName = (MODE_TITLES[item.sessionMode] || item.sessionMode || '').toLowerCase();
    const matchesSearch =
      !q ||
      modeName.includes(q) ||
      (item.name && item.name.toLowerCase().includes(q)) ||
      (item.description && item.description.toLowerCase().includes(q));

    const matchesMode =
      modeFilter === 'all' || item.sessionMode === modeFilter;

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && item.isActive !== false) ||
      (statusFilter === 'inactive' && item.isActive === false);

    return matchesSearch && matchesMode && matchesStatus;
  });

  const totalPages = Math.ceil(filteredSessionTypes.length / itemsPerPage) || 1;
  const paginatedSessionTypes = filteredSessionTypes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Modal Open Handlers
  const handleOpenCreateModal = () => {
    setModalMode('create');
    setEditingId(null);
    setFormData({
      sessionMode: 'video',
      description: '',
      icon: '💻',
      defaultDuration: 50,
      requiresLocation: false,
      displayOrder: sessionTypes.length + 1,
      isActive: true,
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setModalMode('edit');
    setEditingId(item._id);
    setFormData({
      sessionMode: item.sessionMode || 'video',
      description: item.description || '',
      icon: item.icon || (item.sessionMode === 'in-person' ? '🏥' : item.sessionMode === 'video' ? '💻' : '📞'),
      defaultDuration: item.defaultDuration || 50,
      requiresLocation: Boolean(item.requiresLocation),
      displayOrder: item.displayOrder || 0,
      isActive: item.isActive !== false,
    });
    setFormError('');
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    try {
      setFormLoading(true);
      setFormError('');

      const cleanName = MODE_TITLES[formData.sessionMode] || 'Session Type';
      const cleanSlug = formData.sessionMode;

      const payload = {
        name: cleanName,
        slug: cleanSlug,
        sessionMode: formData.sessionMode,
        description: formData.description.trim(),
        icon: formData.icon.trim() || (formData.sessionMode === 'in-person' ? '🏥' : formData.sessionMode === 'video' ? '💻' : '📞'),
        defaultDuration: parseInt(formData.defaultDuration, 10) || 50,
        requiresLocation: formData.sessionMode === 'in-person',
        displayOrder: parseInt(formData.displayOrder, 10) || 0,
        isActive: Boolean(formData.isActive),
      };

      if (modalMode === 'create') {
        const res = await adminApi.createSessionType(payload);
        const newObj = res.sessionType || res;
        setSessionTypes((prev) => [...prev, newObj]);
        showFeedback('success', `Session Type "${cleanName}" created successfully!`);
      } else {
        const res = await adminApi.updateSessionType(editingId, payload);
        const updatedObj = res.sessionType || res;
        setSessionTypes((prev) =>
          prev.map((s) => (s._id === editingId ? { ...s, ...updatedObj } : s))
        );
        showFeedback('success', `Session Type "${cleanName}" updated successfully!`);
      }

      setIsModalOpen(false);
      fetchSessionTypes();
    } catch (err) {
      console.error('Save session type error:', err);
      setFormError(err.message || 'Failed to save session type');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      const newStatus = !item.isActive;
      const res = await adminApi.updateSessionType(item._id, { isActive: newStatus });
      const updated = res.sessionType || res;
      setSessionTypes((prev) =>
        prev.map((s) => (s._id === item._id ? { ...s, ...updated, isActive: newStatus } : s))
      );
      showFeedback('success', `Session type "${item.name}" is now ${newStatus ? 'Active' : 'Inactive'}.`);
    } catch (err) {
      console.error('Toggle status error:', err);
      showFeedback('error', err.message || 'Failed to toggle status');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.sessionType) return;
    try {
      setDeleteModal((prev) => ({ ...prev, loading: true }));
      await adminApi.deleteSessionType(deleteModal.sessionType._id);
      setSessionTypes((prev) => prev.filter((s) => s._id !== deleteModal.sessionType._id));
      showFeedback('success', `Session Type "${deleteModal.sessionType.name}" deleted successfully.`);
      setDeleteModal({ isOpen: false, sessionType: null, loading: false });
    } catch (err) {
      console.error('Delete error:', err);
      showFeedback('error', err.message || 'Failed to delete session type');
      setDeleteModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const getModeBadge = (mode) => {
    switch (mode) {
      case 'in-person':
        return { label: '🏥 In-Person (Clinic)', bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0' };
      case 'video':
        return { label: '💻 Video Consultation', bg: '#eff6ff', text: '#1e40af', border: '#bfdbfe' };
      case 'call':
        return { label: '📞 Audio Call', bg: '#faf5ff', text: '#6b21a8', border: '#e9d5ff' };
      default:
        return { label: mode, bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
    }
  };

  return (
    <>
      <DashboardSidebar userRole="admin" />
      <div className="sidebar-content-wrapper" style={{ padding: '32px 36px', background: '#f8fafc', minHeight: '100vh' }}>
        
        {/* Toast Alert */}
        {feedback.message && (
          <div
            style={{
              position: 'fixed',
              top: '24px',
              right: '24px',
              zIndex: 99999,
              padding: '14px 22px',
              borderRadius: '12px',
              background: feedback.type === 'error' ? '#ef4444' : '#10b981',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '14px',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.25)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <span>{feedback.type === 'error' ? '⚠️' : '✅'}</span>
            <span>{feedback.message}</span>
          </div>
        )}

        {/* Page Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
              🛋️ Session Types Management
            </h1>
            <p style={{ margin: '6px 0 0 0', color: '#64748b', fontSize: '14.5px' }}>
              Configure and manage consultation modes: <strong>In-Person (Clinic)</strong>, <strong>Video Consultation</strong>, and <strong>Audio Call</strong>.
            </p>
          </div>
          <button
            onClick={handleOpenCreateModal}
            style={{
              background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontWeight: 700,
              fontSize: '14.5px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 14px rgba(124, 58, 237, 0.35)',
              transition: 'transform 0.15s ease',
            }}
          >
            <span>➕</span> Add New Session Type
          </button>
        </div>

        {/* Quick Stats Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '18px', marginBottom: '28px' }}>
          {/* Total */}
          <div style={{ background: '#ffffff', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Total Types</div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a', marginTop: '6px' }}>{totalCount}</div>
            <div style={{ fontSize: '12px', color: '#10b981', marginTop: '4px', fontWeight: 600 }}>{activeCount} Active on Platform</div>
          </div>

          {/* In-Person */}
          <div style={{ background: '#ffffff', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#065f46', textTransform: 'uppercase' }}>🏥 In-Person (Clinic)</div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#059669', marginTop: '6px' }}>{inPersonCount}</div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Requires clinic location</div>
          </div>

          {/* Video */}
          <div style={{ background: '#ffffff', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase' }}>💻 Video Consultations</div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#2563eb', marginTop: '6px' }}>{videoCount}</div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Online video room</div>
          </div>

          {/* Call */}
          <div style={{ background: '#ffffff', padding: '20px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#6b21a8', textTransform: 'uppercase' }}>📞 Audio Call Sessions</div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#7c3aed', marginTop: '6px' }}>{callCount}</div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Phone / Voice call</div>
          </div>
        </div>

        {/* Filter Toolbar */}
        <div style={{ background: '#ffffff', padding: '18px 24px', borderRadius: '16px', border: '1px solid #e2e8f0', marginBottom: '24px', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
          
          {/* Search */}
          <div style={{ position: 'relative', flex: '1', minWidth: '260px' }}>
            <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '16px' }}>🔍</span>
            <input
              type="text"
              placeholder="Search session types by name, slug, or details..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: '100%',
                padding: '11px 14px 11px 40px',
                borderRadius: '10px',
                border: '1.5px solid #cbd5e1',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Mode Filter Pills */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { key: 'all', label: '🌟 All Modes' },
              { key: 'in-person', label: '🏥 In-Person' },
              { key: 'video', label: '💻 Video' },
              { key: 'call', label: '📞 Call' },
            ].map((m) => (
              <button
                key={m.key}
                onClick={() => {
                  setModeFilter(m.key);
                  setCurrentPage(1);
                }}
                style={{
                  padding: '8px 14px',
                  borderRadius: '10px',
                  border: modeFilter === m.key ? '1.5px solid #7c3aed' : '1px solid #cbd5e1',
                  background: modeFilter === m.key ? '#f5f3ff' : '#ffffff',
                  color: modeFilter === m.key ? '#7c3aed' : '#475569',
                  fontWeight: modeFilter === m.key ? 700 : 500,
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                {m.label}
              </button>
            ))}
          </div>

          {/* Status Filter */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: '9px 14px',
                borderRadius: '10px',
                border: '1.5px solid #cbd5e1',
                fontSize: '13.5px',
                background: '#ffffff',
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>

        {/* Data Table */}
        <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
              <div style={{ fontWeight: 600 }}>Loading session types...</div>
            </div>
          ) : filteredSessionTypes.length === 0 ? (
            <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>
              <div style={{ fontSize: '42px', marginBottom: '12px' }}>🛋️</div>
              <h3 style={{ margin: '0 0 6px 0', color: '#1e293b' }}>No Session Types Found</h3>
              <p style={{ margin: '0 0 18px 0', fontSize: '14px' }}>
                {searchQuery || modeFilter !== 'all' ? 'Try adjusting your filters or search query.' : 'Create your first session type to get started.'}
              </p>
              <button
                onClick={handleOpenCreateModal}
                style={{
                  padding: '10px 20px',
                  background: '#7c3aed',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                ➕ Add Session Type
              </button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}>
                    <th style={{ padding: '14px 20px', fontWeight: 700, color: '#475569', fontSize: '12.5px', textTransform: 'uppercase' }}>Session Type</th>
                    <th style={{ padding: '14px 20px', fontWeight: 700, color: '#475569', fontSize: '12.5px', textTransform: 'uppercase' }}>Mode</th>
                    <th style={{ padding: '14px 20px', fontWeight: 700, color: '#475569', fontSize: '12.5px', textTransform: 'uppercase' }}>Default Duration</th>
                    <th style={{ padding: '14px 20px', fontWeight: 700, color: '#475569', fontSize: '12.5px', textTransform: 'uppercase' }}>Location Req.</th>
                    <th style={{ padding: '14px 20px', fontWeight: 700, color: '#475569', fontSize: '12.5px', textTransform: 'uppercase' }}>Order</th>
                    <th style={{ padding: '14px 20px', fontWeight: 700, color: '#475569', fontSize: '12.5px', textTransform: 'uppercase' }}>Status</th>
                    <th style={{ padding: '14px 20px', fontWeight: 700, color: '#475569', fontSize: '12.5px', textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSessionTypes.map((item) => {
                    const badge = getModeBadge(item.sessionMode);
                    return (
                      <tr
                        key={item._id}
                        style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s ease' }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#fafafa')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        {/* Name & Icon */}
                        <td style={{ padding: '16px 20px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '26px' }}>{item.icon || '🛋️'}</span>
                            <div>
                              <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '14.5px' }}>{item.name || MODE_TITLES[item.sessionMode] || item.sessionMode}</div>
                              {item.description && (
                                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px', maxWidth: '320px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {item.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Mode */}
                        <td style={{ padding: '16px 20px' }}>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: '5px 12px',
                              borderRadius: '20px',
                              background: badge.bg,
                              color: badge.text,
                              border: `1px solid ${badge.border}`,
                              fontWeight: 700,
                              fontSize: '12.5px',
                            }}
                          >
                            {badge.label}
                          </span>
                        </td>

                        {/* Duration */}
                        <td style={{ padding: '16px 20px' }}>
                          <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '14px' }}>⏱️ {item.defaultDuration || 50} mins</div>
                        </td>

                        {/* Location Req */}
                        <td style={{ padding: '16px 20px' }}>
                          {item.requiresLocation || item.sessionMode === 'in-person' ? (
                            <span style={{ color: '#065f46', fontSize: '12.5px', fontWeight: 700 }}>🏥 Clinic Required</span>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '12.5px' }}>🌐 Online / Virtual</span>
                          )}
                        </td>

                        {/* Order */}
                        <td style={{ padding: '16px 20px', color: '#64748b', fontWeight: 600 }}>
                          #{item.displayOrder || 0}
                        </td>

                        {/* Status Toggle */}
                        <td style={{ padding: '16px 20px' }}>
                          <button
                            type="button"
                            onClick={() => handleToggleActive(item)}
                            style={{
                              padding: '5px 12px',
                              borderRadius: '20px',
                              border: item.isActive !== false ? '1px solid #a7f3d0' : '1px solid #fecaca',
                              background: item.isActive !== false ? '#ecfdf5' : '#fef2f2',
                              color: item.isActive !== false ? '#065f46' : '#991b1b',
                              fontWeight: 700,
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <span>{item.isActive !== false ? '● Active' : '○ Inactive'}</span>
                          </button>
                        </td>

                        {/* Actions */}
                        <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button
                              onClick={() => handleOpenEditModal(item)}
                              title="Edit Session Type"
                              style={{
                                padding: '6px 12px',
                                borderRadius: '8px',
                                border: '1px solid #cbd5e1',
                                background: '#ffffff',
                                color: '#1e293b',
                                fontWeight: 600,
                                fontSize: '12.5px',
                                cursor: 'pointer',
                              }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => setDeleteModal({ isOpen: true, sessionType: item, loading: false })}
                              title="Delete Session Type"
                              style={{
                                padding: '6px 12px',
                                borderRadius: '8px',
                                border: '1px solid #fecaca',
                                background: '#fef2f2',
                                color: '#dc2626',
                                fontWeight: 600,
                                fontSize: '12.5px',
                                cursor: 'pointer',
                              }}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && filteredSessionTypes.length > 0 && (
            <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9' }}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredSessionTypes.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(sz) => {
                  setItemsPerPage(sz);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </div>

        {/* ========================================================================= */}
        {/* CREATE / EDIT MODAL */}
        {/* ========================================================================= */}
        {isModalOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(5px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '20px',
            }}
            onClick={() => !formLoading && setIsModalOpen(false)}
          >
            <div
              style={{
                background: '#ffffff',
                borderRadius: '20px',
                padding: '28px',
                maxWidth: '620px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1px solid #f1f5f9', paddingBottom: '14px' }}>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#1e293b' }}>
                  {modalMode === 'create' ? '➕ Add Session Mode' : `✏️ Edit ${MODE_TITLES[formData.sessionMode] || 'Session Type'}`}
                </h3>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  style={{ border: 'none', background: '#f1f5f9', borderRadius: '50%', width: '32px', height: '32px', fontSize: '1rem', cursor: 'pointer', color: '#64748b' }}
                >
                  ✕
                </button>
              </div>

              {formError && (
                <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#dc2626', fontSize: '0.88rem', marginBottom: '18px', fontWeight: 600 }}>
                  ⚠️ {formError}
                </div>
              )}

              <form onSubmit={handleFormSubmit}>
                
                {/* Session Mode Selector */}
                <div style={{ marginBottom: '18px' }}>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 800, color: '#1e293b', marginBottom: '8px' }}>
                    Select Session Mode <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                    {[
                      { mode: 'in-person', icon: '🏥', label: 'In-Person', sub: 'Clinic / Offline' },
                      { mode: 'video', icon: '💻', label: 'Video', sub: 'Online Encrypted' },
                      { mode: 'call', icon: '📞', label: 'Audio Call', sub: 'Voice / Phone' },
                    ].map((m) => {
                      const isSelected = formData.sessionMode === m.mode;
                      return (
                        <button
                          key={m.mode}
                          type="button"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              sessionMode: m.mode,
                              icon: prev.icon || m.icon,
                              requiresLocation: m.mode === 'in-person',
                            }));
                          }}
                          style={{
                            padding: '12px 10px',
                            borderRadius: '12px',
                            border: isSelected ? '2px solid #7c3aed' : '1.5px solid #cbd5e1',
                            background: isSelected ? '#f5f3ff' : '#ffffff',
                            color: isSelected ? '#7c3aed' : '#475569',
                            cursor: 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <div style={{ fontSize: '22px', marginBottom: '4px' }}>{m.icon}</div>
                          <div style={{ fontWeight: 700, fontSize: '13px' }}>{m.label}</div>
                          <div style={{ fontSize: '11px', color: isSelected ? '#6d28d9' : '#94a3b8' }}>{m.sub}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Icon Selection */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Icon / Emoji
                  </label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      style={{ width: '56px', padding: '8px', textAlign: 'center', fontSize: '20px', borderRadius: '8px', border: '1.5px solid #cbd5e1', outline: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {POPULAR_SESSION_ICONS.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => setFormData({ ...formData, icon: emoji })}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            border: formData.icon === emoji ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                            background: formData.icon === emoji ? '#f5f3ff' : '#ffffff',
                            fontSize: '18px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Duration Presets */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Default Duration (Minutes)
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="480"
                    value={formData.defaultDuration}
                    onChange={(e) => setFormData({ ...formData, defaultDuration: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    {DURATION_PRESETS.map((dur) => (
                      <button
                        key={dur}
                        type="button"
                        onClick={() => setFormData({ ...formData, defaultDuration: dur })}
                        style={{
                          padding: '5px 12px',
                          borderRadius: '8px',
                          border: parseInt(formData.defaultDuration, 10) === dur ? '1.5px solid #7c3aed' : '1px solid #e2e8f0',
                          background: parseInt(formData.defaultDuration, 10) === dur ? '#f5f3ff' : '#ffffff',
                          color: parseInt(formData.defaultDuration, 10) === dur ? '#7c3aed' : '#64748b',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        {dur} Mins
                      </button>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Description & Features
                  </label>
                  <textarea
                    rows="3"
                    placeholder="Provide overview of this session mode (preparation instructions, equipment required, etc.)..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #cbd5e1', fontSize: '0.9rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                  ></textarea>
                </div>

                {/* Settings & Active Status */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '22px', background: '#f8fafc', padding: '14px 16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', marginBottom: '4px' }}>
                      Display Sort Order
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.displayOrder}
                      onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                      style={{ width: '100px', padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem', outline: 'none' }}
                    />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem', color: '#1e293b' }}>
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span>Active on Platform</span>
                    </label>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    disabled={formLoading}
                    style={{ padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #cbd5e1', background: '#ffffff', color: '#475569', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formLoading}
                    style={{
                      padding: '10px 24px',
                      borderRadius: '10px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
                      color: '#ffffff',
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: '0 4px 14px rgba(124, 58, 237, 0.3)',
                    }}
                  >
                    {formLoading ? 'Saving...' : modalMode === 'create' ? '🚀 Create Session Type' : '💾 Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* DELETE CONFIRMATION MODAL */}
        {/* ========================================================================= */}
        {deleteModal.isOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(5px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999,
              padding: '20px',
            }}
            onClick={() => !deleteModal.loading && setDeleteModal({ isOpen: false, sessionType: null, loading: false })}
          >
            <div
              style={{
                background: '#ffffff',
                borderRadius: '20px',
                padding: '28px',
                maxWidth: '460px',
                width: '100%',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
                textAlign: 'center',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🗑️</div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 800, color: '#0f172a' }}>
                Delete Session Type?
              </h3>
              <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: '14px', lineHeight: 1.5 }}>
                Are you sure you want to permanently delete{' '}
                <strong style={{ color: '#0f172a' }}>"{deleteModal.sessionType?.name}"</strong>?
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setDeleteModal({ isOpen: false, sessionType: null, loading: false })}
                  disabled={deleteModal.loading}
                  style={{ padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #cbd5e1', background: '#ffffff', color: '#475569', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  disabled={deleteModal.loading}
                  style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', background: '#ef4444', color: '#ffffff', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(239, 68, 68, 0.35)' }}
                >
                  {deleteModal.loading ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default AdminSessionTypesPage;
